import React from 'react'
import CIcon from '@coreui/icons-react'
import {
  cilApplicationsSettings,
  cilSpeedometer,
  cilUser,
  cilPeople,
  cilFolder,
  cilBuilding,
  cilSettings,
  cilChartLine,
} from '@coreui/icons'
import { CNavGroup, CNavItem, CNavTitle } from '@coreui/react'

// Helper to get user from localStorage
const getUser = () => {
  try {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  } catch {
    return null
  }
}

// Check if user is internal (SSO)
const isInternalUser = () => {
  const user = getUser()
  return user?.userType === 'internal'
}

// Check if user has specific role
const hasRole = (roleCodes) => {
  const user = getUser()
  if (!user) return false

  // Check if user has roleid directly (e.g., 'R-001')
  if (user.roleid && roleCodes.includes(user.roleid)) {
    return true
  }

  // Check if user has role object with code
  if (user.role && user.role.code && roleCodes.includes(user.role.code)) {
    return true
  }

  return false
}

// Navigation items for Internal Users (SSO - Company Employees)
const internalNav = [
  {
    component: CNavItem,
    name: 'Dashboard',
    to: '/dashboard',
    icon: <CIcon icon={cilSpeedometer} customClassName="nav-icon" />,
  },
  {
    component: CNavTitle,
    name: 'Work',
  },
  {
    component: CNavItem,
    name: 'Projects',
    to: '/projects',
    icon: <CIcon icon={cilFolder} customClassName="nav-icon" />,
  },
  {
    component: CNavItem,
    name: 'My Profile',
    to: '/Users/profile',
    icon: <CIcon icon={cilUser} customClassName="nav-icon" />,
  },
  {
    component: CNavItem,
    name: 'My Group',
    to: '/customer-group',
    icon: <CIcon icon={cilBuilding} customClassName="nav-icon" />,
  },
]

// Navigation items for External Users (Customer Accounts)
const externalNav = [
  {
    component: CNavItem,
    name: 'Dashboard',
    to: '/dashboard',
    icon: <CIcon icon={cilSpeedometer} customClassName="nav-icon" />,
  },
  {
    component: CNavTitle,
    name: 'My Work',
  },
  {
    component: CNavItem,
    name: 'Projects',
    to: '/projects',
    icon: <CIcon icon={cilFolder} customClassName="nav-icon" />,
  },
  {
    component: CNavItem,
    name: 'My Profile',
    to: '/Users/profile',
    icon: <CIcon icon={cilUser} customClassName="nav-icon" />,
  },
  {
    component: CNavItem,
    name: 'My Group',
    to: '/customer-group',
    icon: <CIcon icon={cilBuilding} customClassName="nav-icon" />,
  },
]

// StreamPixel analytics — the API allows admins (all projects) and customer owners (their scope)
const analyticsItem = {
  component: CNavItem,
  name: 'Stream Analytics',
  to: '/analytics',
  icon: <CIcon icon={cilChartLine} customClassName="nav-icon" />,
}

// Navigation items for Admin Users (Super Admin, Admin)
const adminNav = [
  {
    component: CNavItem,
    name: 'Dashboard',
    to: '/dashboard',
    icon: <CIcon icon={cilSpeedometer} customClassName="nav-icon" />,
  },
  analyticsItem,
  {
    component: CNavTitle,
    name: 'Work',
  },
  {
    component: CNavItem,
    name: 'Projects',
    to: '/projects',
    icon: <CIcon icon={cilFolder} customClassName="nav-icon" />,
  },
  {
    component: CNavItem,
    name: 'My Profile',
    to: '/Users/profile',
    icon: <CIcon icon={cilUser} customClassName="nav-icon" />,
  },
  {
    component: CNavItem,
    name: 'My Group',
    to: '/customer-group',
    icon: <CIcon icon={cilBuilding} customClassName="nav-icon" />,
  },
  {
    component: CNavTitle,
    name: 'Administration',
  },
  {
    component: CNavGroup,
    name: 'Manage',
    icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
    items: [
      {
        component: CNavItem,
        name: 'Users',
        to: '/administrator/users',
        icon: <CIcon icon={cilPeople} customClassName="nav-icon" />,
      },
      {
        component: CNavItem,
        name: 'Projects',
        to: '/administrator/projects',
        icon: <CIcon icon={cilApplicationsSettings} customClassName="nav-icon" />,
      },
      {
        component: CNavItem,
        name: 'Stream Types',
        to: '/administrator/stream-types',
        icon: <CIcon icon={cilSettings} customClassName="nav-icon" />,
      },
      {
        component: CNavItem,
        name: 'Customer Groups',
        to: '/administrator/customer-groups',
        icon: <CIcon icon={cilBuilding} customClassName="nav-icon" />,
      },
    ],
  },
]

// Export function to get navigation based on user type and role
const getNavigation = () => {
  const user = getUser()

  if (!user) {
    // Not logged in - return minimal nav
    return []
  }

  // Admin roles: R-001 (Super Admin), R-002 (Admin) — must match the backend admin set
  // (SUPPER/ADMIN). QA (R-003) is a normal role in the API, so it gets the normal nav.
  if (hasRole(['R-001', 'R-002'])) {
    return adminNav
  }

  const base = user.userType === 'internal' ? internalNav : externalNav // SSO staff vs customer accounts
  // Customer owners (R-006) see analytics for their own group and projects
  return hasRole(['R-006']) ? [base[0], analyticsItem, ...base.slice(1)] : base
}

// Default export for backward compatibility
const _nav = adminNav

export default _nav
export { getNavigation, internalNav, externalNav, adminNav, isInternalUser, hasRole }